'use client';

import { FAQ } from '@/components/FAQ';
import Link from 'next/link';

export interface RepairReplaceCalculatorFAQsProps {
	testId?: string;
}

const RepairReplaceCalculatorFAQs = (
	props: RepairReplaceCalculatorFAQsProps,
) => {
	const { testId = 'RepairReplaceCalculatorFAQs' } = props;

	return (
		<FAQ testId={testId}>
			<FAQ.Title>FAQ</FAQ.Title>
			<FAQ.Block>
				<FAQ.Item>
					<FAQ.Question panelId="1">
						How does your HVAC repair or replacement cost calculator work?
					</FAQ.Question>
					<FAQ.Answer panelId="1">
						<p className="mb-7">
							Our repair or replace calculator is tailored to meet homeowner
							needs and incorporates insights from our Technical Advisors. It
							evaluates the system&rsquo;s age, applies a $3,000 repair cost
							threshold, and considers the system&rsquo;s warranty status.
						</p>
						<ul className="mb-7 ml-10 list-outside list-disc">
							<li>
								<strong>For systems under 5 years old:</strong> Repairs are
								usually recommended especially if there&rsquo;s an active
								warranty that covers significant repair costs.
							</li>
							<li>
								<strong>
									For systems under 10 years old but older than 5 years:
								</strong>{' '}
								Repairs are typically suggested, as parts might still be covered
								under a standard manufacturer&rsquo;s warranty if the equipment
								was registered and you were the homeowner at the time of
								installation.
							</li>
							<li>
								<strong>
									For systems over 10 years old with a repair cost under $3,000:
								</strong>{' '}
								In this scenario, a repair might still be the best choice,
								particularly if the repair is for a one-time fix like replacing
								a blower motor or coil.
							</li>
							<li>
								<strong>
									For any system where the repair cost exceeds the $3,000
									threshold:
								</strong>{' '}
								Replacement may be more economical, but considerations such as
								one-time fixes and overall system performance should be
								evaluated.
							</li>
							<li>
								<strong>For any system over 15 years old:</strong> Air
								conditioners and furnaces can last up to 20 years, while heat
								pumps have a lifespan of up to 15 years. As HVAC systems age,
								they become more prone to needing repairs; however, you may want
								to weigh the repair cost against the price of a new system,
								especially if you use your HVAC system less frequently.
							</li>
						</ul>
						<p>
							For detailed guidance tailored to your specific HVAC system,
							including warranty details and a comprehensive cost-benefit
							analysis, consult with our Technical Advisors. They can help
							ensure that you make the best decision based on your home&rsquo;s
							particular needs and the long-term health of your HVAC system.
						</p>
					</FAQ.Answer>
				</FAQ.Item>
				<FAQ.Item>
					<FAQ.Question panelId="2">
						What if I only need a minor fix?
					</FAQ.Question>
					<FAQ.Answer panelId="2">
						<p>
							Minor HVAC repairs costing less than $500 generally warrant a
							repair, unless your system is older and frequently breaks down. In
							these cases, applying the $5,000 rule can help you decide whether
							to repair or replace your HVAC system.
						</p>
					</FAQ.Answer>
				</FAQ.Item>
				<FAQ.Item>
					<FAQ.Question panelId="4">
						Should I consider an HVAC replacement if I have a warranty?
					</FAQ.Question>
					<FAQ.Answer panelId="4">
						<p>
							Always check your warranty first. If it covers the needed repairs,
							especially costly parts or labor, HVAC repair is likely your best
							choice. However, if repair costs approach 50% of the price of a
							new system, consider replacement, particularly for older models.
						</p>
					</FAQ.Answer>
				</FAQ.Item>
				<FAQ.Item>
					<FAQ.Question panelId="5">
						How long does an HVAC system last?
					</FAQ.Question>
					<FAQ.Answer panelId="5">
						<p>
							An HVAC system typically lasts about 10 to 25 years on average
							based on factors such as usage, maintenance history, and the
							climate you live in. In moderate climates with less extreme
							temperatures, your HVAC system may last longer due to less strain
							on the equipment. Meanwhile, units in regions with more extreme
							weather may experience accelerated wear and tear, leading to a
							potentially shorter lifespan. Performing regular maintenance and
							making timely repairs is the key to improving efficiency and
							prolonging the life of your HVAC system.
						</p>
					</FAQ.Answer>
				</FAQ.Item>
				<FAQ.Item>
					<FAQ.Question panelId="6">
						How can I tell if my HVAC system isn&apos;t working properly?
					</FAQ.Question>
					<FAQ.Answer panelId="6">
						<p>
							Watch for reduced airflow, high energy bills, difficulty reaching
							desired temperatures, and frequent on-off cycles. These are signs
							your system may be becoming less efficient and could need HVAC
							repair or replacement.
						</p>
					</FAQ.Answer>
				</FAQ.Item>
				<FAQ.Item>
					<FAQ.Question panelId="7">
						What do I need to know about HVAC energy efficiency?
					</FAQ.Question>
					<FAQ.Answer panelId="7">
						<p>
							Efficiency is measured by SEER, AFUE, and HSPF ratings. Older
							systems or those with low ratings use more energy, resulting in
							higher utility bills. If your system&apos;s SEER rating is below
							10, consider replacing the HVAC system to reduce energy costs. As
							a rule of thumb, if your system is older than 2006, your SEER is
							most likely 10 or below.
						</p>
					</FAQ.Answer>
				</FAQ.Item>
				<FAQ.Item>
					<FAQ.Question panelId="8">
						Why should I consider replacing my system if it uses R-22
						refrigerant?
					</FAQ.Question>
					<FAQ.Answer panelId="8">
						<p>
							If your HVAC system uses ozone-depleting R-22, the high cost of
							this phased-out refrigerant combined with any significant repair
							needs often makes replacement a better option (and sometimes
							necessary if you can&apos;t find the required refrigerant).
							Replacing an HVAC system that uses R-22 can save money and reduce
							your environmental impact.
						</p>
					</FAQ.Answer>
				</FAQ.Item>
				<FAQ.Item>
					<FAQ.Question panelId="9">
						Can you help me replace my HVAC system?
					</FAQ.Question>
					<FAQ.Answer panelId="9">
						<p>
							Absolutely! We’re a tech company bringing a breath of fresh air to
							the HVAC world.{' '}
							<Link
								href="/replace/virtual/"
								className="text-sky-700 hover:text-sky-800 hover:underline"
							>
								Replace your HVAC
							</Link>{' '}
							with us and enjoy a customized solution with upfront pricing. Get
							an honest quote quickly by submitting a little information in just
							few easy steps. Our team will craft a custom solution for your
							home, ensuring you get the trusted help you deserve with fair,
							honest pricing.
						</p>
					</FAQ.Answer>
				</FAQ.Item>
			</FAQ.Block>
		</FAQ>
	);
};

export default RepairReplaceCalculatorFAQs;
